import React, { useState, useEffect } from 'react';
import { Add_industry, List_industry, Delete_industry, Update_industry } from '../../Utils/Api';
import { toast } from 'react-toastify';

const Industries = () => {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ title: '' });
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Fetch industries on component mount
  useEffect(() => {
    fetchIndustries();
  }, []);

  // Fetch all industries
  const fetchIndustries = async () => {
    setLoading(true);
    try {
      const response = await List_industry();
      if (response && response.data.data) {
        setIndustries(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
      toast.error('Failed to fetch industries');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle form submission (Add/Update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Please enter industry title');
      return;
    }

    setLoading(true);
    try {
      if (editingId) {
        // Update existing industry
        await Update_industry(editingId, formData);
        toast.success('Industry updated successfully');
      } else {
        // Add new industry
        await Add_industry(formData);
        toast.success('Industry added successfully');
      }

      // Reset form and refresh list
      setFormData({ title: '' });
      setEditingId(null);
      setShowModal(false);
      fetchIndustries();
    } catch (error) {
      console.error('Error saving industry:', error);
      toast.error('Failed to save industry');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (industry) => {
    setFormData({ title: industry.title });
    setEditingId(industry._id || industry.id);
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteId) return;

    setLoading(true);
    try {
      await Delete_industry(deleteId);
      toast.success('Industry deleted successfully');
      setDeleteId(null);
      fetchIndustries();
    } catch (error) {
      console.error('Error deleting industry:', error);
      toast.error('Failed to delete industry');
    } finally {
      setLoading(false);
    }
  };

  // Open add modal
  const handleAdd = () => {
    setFormData({ title: '' });
    setEditingId(null);
    setShowModal(true);
  };

  return (
    <div className="main-content ">
      <div className="row my-4 mx-1">
        <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
          <h1 className="page-title fw-semibold fs-18 mb-0 mt-2">Industries</h1>
          <div className="ms-md-1 ms-0 ">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><a href="#">Dashboard</a></li>
                <li className="breadcrumb-item active" aria-current="page">Industries</li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="card custom-card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h6 className="card-title mb-0">Industries List</h6>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleAdd}
            >
              <i className="ri-add-line me-1"></i> Add Industry
            </button>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table text-nowrap">
                <thead className="table-primary">
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">Industry Title</th>
                    <th scope="col">Created Date</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="4" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading && industries.length === 0 && (
                    <tr>
                      <td colSpan="4" className="text-center py-4">
                        No industries found
                      </td>
                    </tr>
                  )}

                  {!loading && industries.map((industry, index) => (
                    <tr key={industry._id || industry.id}>
                      <th scope="row">{index + 1}</th>
                      <td>{industry.title}</td>
                      <td>{new Date(industry.createdAt || Date.now()).toLocaleDateString()}</td>
                      <td>
                        <div className="hstack gap-2 fs-15">
                          <button
                            onClick={() => handleEdit(industry)}
                            className="btn btn-icon btn-sm btn-info-transparent rounded-pill"
                            title="Edit"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            onClick={() => setDeleteId(industry._id || industry.id)}
                            className="btn btn-icon btn-sm btn-danger-transparent rounded-pill"
                            data-bs-toggle="modal"
                            data-bs-target="#deleteModal"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingId ? 'Edit Industry' : 'Add New Industry'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label htmlFor="title" className="form-label">Industry Title</label>
                      <input
                        type="text"
                        className="form-control"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Enter industry title"
                        required
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          {editingId ? 'Updating...' : 'Adding...'}
                        </>
                      ) : (
                        editingId ? 'Update' : 'Add'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <div className="modal fade" id="deleteModal" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  onClick={() => setDeleteId(null)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this industry?</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                  onClick={() => setDeleteId(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  data-bs-dismiss="modal"
                  onClick={handleDelete}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Industries;
